import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, LogOut, LayoutDashboard, Truck } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop/rice", label: "Shop Rice" },
  { to: "/shop/grains", label: "Grains" },
  { to: "/shop/pulses", label: "Pulses" },
  { to: "/trade", label: "Trade Your Rice" },
];

const accountLinks = [
  { to: "/ricepoints", label: "My RicePoints" },
  { to: "/orders", label: "My Orders" },
  { to: "/account", label: "Account" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `text-sm font-semibold transition-colors ${isActive ? "text-primary" : "text-foreground/70 hover:text-primary"}`;

  const isStaff = user && user.role === "staff";
  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");

  return (
    <header className="sticky top-0 z-50 rb-glass border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center gap-6">
        <Link to="/" className="shrink-0" data-testid="brand-logo">
          <BrandMark iconSize={38} />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
          {user &&
            accountLinks.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>
                {l.label}
              </NavLink>
            ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {isStaff && (
            <Button variant="outline" size="sm" onClick={() => navigate("/staff")} data-testid="nav-staff-btn" className="hidden sm:flex">
              <Truck size={16} /> Pickups
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="nav-admin-btn" className="hidden sm:flex">
              <LayoutDashboard size={16} /> Admin
            </Button>
          )}

          <Link to="/cart" className="relative p-2 rounded-md hover:bg-muted" data-testid="nav-cart-btn">
            <ShoppingCart size={20} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center font-mono-points" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="hidden sm:flex">
              <LogOut size={16} /> Logout
            </Button>
          ) : (
            <Button size="sm" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="hidden sm:flex">
              Login
            </Button>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 rounded-md hover:bg-muted" data-testid="mobile-menu-btn"><Menu size={22} /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] sm:w-80">
              <div className="mt-6 flex flex-col gap-1">
                {[...links, ...(user ? accountLinks : [])].map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink to={l.to} className="px-3 py-3 rounded-md text-base font-semibold hover:bg-muted" data-testid={`mnav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                {isStaff && <SheetClose asChild><NavLink to="/staff" className="px-3 py-3 rounded-md font-semibold hover:bg-muted">Staff Pickups</NavLink></SheetClose>}
                {isAdmin && <SheetClose asChild><NavLink to="/admin" className="px-3 py-3 rounded-md font-semibold hover:bg-muted">Admin Dashboard</NavLink></SheetClose>}
                <div className="mt-4 border-t pt-4">
                  {user ? (
                    <Button className="w-full" variant="outline" onClick={() => { setOpen(false); logout(); navigate("/"); }} data-testid="mobile-logout-btn"><LogOut size={16} /> Logout</Button>
                  ) : (
                    <Button className="w-full" onClick={() => { setOpen(false); navigate("/login"); }} data-testid="mobile-login-btn">Login / Register</Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
